import { booleanAttribute, Injectable } from "@angular/core";
import { firstValueFrom, fromEvent, Subject } from "rxjs";
import { filter, take } from "rxjs/operators";
import {deleteCookie} from "../shared/util/cookies";
import {ClockService} from "../shared/util/clock.service";
import {AuthService, Country} from "../../swaggerServices";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import * as Msal from '@azure/msal-browser';
import { InteractionStatus } from "@azure/msal-browser";
import {environment} from "../environments/environment";

const cookieName = 'token';
const loggedInLSName = 'loggedIn';
const adTokens = 'adTokens';
const adLogin = 'adLogin';
let isAdTokenRefeshPollingRunning = false;
let silentRq: Msal.SilentRequest;

export interface MsalToken {
  idToken: string;
  accessToken: string;
  homeAccountId: string;
  lastUpdate: number;
  username: string;
}

@Injectable({ providedIn: 'root' })
export class UserInfoService {
  id: number;
  firstName: string;
  lastName: string;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isClientAdmin: boolean;
  groups: string[];
  countries: Country[];
  loggedIn: boolean;
  showLoginPage: boolean;
  subscribeRunning: boolean;
  timeoutHandle?: any;
  loginStateChanged: Subject<boolean>;
  intervalHandler: number;
  private readonly scopes = [`${environment.adClientId}/.default`, "email"];

  constructor(
    private clock: ClockService,
    private authService: AuthService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {
    this.reset();
    this.loginStateChanged = new Subject<boolean>();
    const token = this.getTokenFromCookie();
    this.showLoginPage = token ? !this.setInfoFromToken(token) : true;

    // Take into account authentication that happens in other tabs and run post
    // auth logic so that the current tab will run also function as if it's logged in
    fromEvent<StorageEvent>(window, 'storage')
      .subscribe((storageEvent) => {
        if (storageEvent.storageArea !== localStorage) {
          return;
        }

        if (
          storageEvent.key === loggedInLSName &&
          booleanAttribute(storageEvent.newValue) === true &&
          booleanAttribute(storageEvent.oldValue) !== true
        ) {
          // isLoggedIn is called here because actually has a side effect, rather
          // than being a simple login status check as the function name implies.
          // isLoggedIn also sets up polling for AD Token renewal if necessary.
          this.isLoggedIn();
          const token = this.getTokenFromCookie();
          if (token) {
            this.setInfoFromToken(token);
          }
        }
      });
  }

  private async ensureMsalReady(): Promise<void> {
    await firstValueFrom(
      this.msalBroadcastService.inProgress$.pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1)
      )
    );
  }

  reset() {
    this.id = -1;
    this.firstName = '';
    this.lastName = '';
    this.isSuperAdmin = false;
    this.isAdmin = false;
    this.isClientAdmin = false;
    this.loggedIn = false;
    this.countries = [];
  }

  getTokenFromCookie(): string {
    const prefix = `${cookieName}=`;
    const tokens = document.cookie.split(';')
      .map(s => s.trim())
      .filter(s => s.indexOf(prefix) === 0)
      .map(s => s.slice(prefix.length));
    return tokens.length > 0 ? tokens[tokens.length - 1] : '';
  }

  async silentAdTokenRenew(curr: MsalToken) {
    await this.ensureMsalReady();

    silentRq = {
      account: this.msalService.instance.getAccount({ homeAccountId: curr.homeAccountId }),
      scopes: this.scopes,
    };

    this.msalService.acquireTokenSilent(silentRq).subscribe({
      next: result => {
        if (result.accessToken !== curr.accessToken) {
          curr.idToken = result.idToken;
          curr.accessToken = result.accessToken;
          curr.homeAccountId = result.account.homeAccountId;
        }

        curr.lastUpdate = Date.now();
        localStorage.setItem(adTokens, JSON.stringify(curr));
      },
      error: error => {
        return null;
      }
    });
  }

  setAdTokens(msalResponse: Omit<MsalToken, 'lastUpdate'>){
    if(!isAdTokenRefeshPollingRunning){
      localStorage.setItem(adLogin, 'true');
      const tokenData: MsalToken = {
        idToken: msalResponse.idToken,
        accessToken: msalResponse.accessToken,
        homeAccountId: msalResponse.homeAccountId,
        username: msalResponse.username,
        lastUpdate: Date.now(),
      };
      localStorage.setItem(adTokens, JSON.stringify(tokenData));
      isAdTokenRefeshPollingRunning = true;
      this.intervalHandler = window.setInterval(async () => {
        let currentToken: MsalToken;
        try {
          currentToken = JSON.parse(localStorage.getItem(adTokens));
          if(!currentToken){
            this.logout()
          }
        } catch (e) {
          this.logout()
        }
        // Refresh every 10 minutes
        if (currentToken && Date.now() - currentToken.lastUpdate > 60000 * 10) {
            await this.silentAdTokenRenew(currentToken);
        }
      }, 60000);
    }

  }

  decodeToken(token: string) {
    const tokenParts = token.split(".");
    if (tokenParts.length !== 3) return false;

    const jwtPart = tokenParts[1];
    const data = JSON.parse(atob(jwtPart));
    return data
  }

  setInfoFromToken(token: string): boolean {
    const data = this.decodeToken(token);

    this.id = data.id;
    this.firstName = data.firstName || '';
    this.lastName = data.lastName || '';
    this.isSuperAdmin = data.isSuperAdmin || false;
    this.isAdmin = data.isAdmin || false;
    this.isClientAdmin = data.isClientAdmin || false;
    this.groups = data.groups || [];
    this.countries = data.countries || [];

    const wasLoggedIn = this.loggedIn;
    this.loggedIn = true;
    this.showLoginPage = false;

    const expiryDate = new Date(data.exp * 1000);
    const expiryCheckMs = Math.max(5000, (expiryDate.getTime() - this.clock.now()) / 2);

    if (this.timeoutHandle !== undefined) {
      this.clock.clearTimeout(this.timeoutHandle);
    }
    this.timeoutHandle = this.clock.setTimeout(() => {
      this.timeoutHandle = undefined;
      // we are expiring now, check the cookie again to see if we're ok
      const newData = this.decodeToken(this.getTokenFromCookie());
      const newExpireCheck = new Date(newData.exp * 1000).valueOf() - Date.now().valueOf();
      const loggedIn = newExpireCheck > 0 ? true:false

      if (!loggedIn) {
        this.clearAndEmitLoginState();
      }
    }, expiryCheckMs);

    if (!wasLoggedIn) {
      this.loginStateChanged.next(true);
    }
    window.localStorage.setItem(loggedInLSName, 'true');
    sessionStorage.clear()
    return true;
  }

  isLoggedIn() {
    const loggedIn = window.localStorage.getItem(loggedInLSName);
    if(window.localStorage.getItem(adLogin) === 'true'){
      try {
        const currentAdToken = JSON.parse(localStorage.getItem(adTokens));
        this.setAdTokens(currentAdToken);
      } catch (e) {
         this.logout()
      }
    }
    return loggedIn === 'true' ? true : false;
  }

  public clearAndEmitLoginState() {
    if (this.loggedIn) {
      this.loggedIn = false;
      this.clearStoredTokens();
      this.loginStateChanged.next(false);
    }
  }

  redirectToLoginScreen(windowObj: Window = window){
    const href = windowObj.location.pathname.startsWith("/ess") ? "ess/" :  windowObj.location.pathname.startsWith("/rubix") ? "login/" : "/";
    windowObj.location.href = href;
  }

  clearStoredTokens(){
    window.localStorage.removeItem(adTokens);
    window.localStorage.removeItem(adLogin);
    window.localStorage.removeItem(loggedInLSName);
  }

  logout(adLogin=false) {
    this.authService.getLogoutAuths().subscribe(async () => {
      // NOTE(mike): we should call reset() if we decide to not reload the page via location.href
      // this.reset();
      this.subscribeRunning = false;
      if(this.intervalHandler){
        clearInterval(this.intervalHandler);
      }

      this.clearStoredTokens();
      try {
        await this.ensureMsalReady();
        if (this.msalService.instance.getActiveAccount() !== null || adLogin) {
          this.msalService.logoutPopup({ mainWindowRedirectUri: "/login" });
        } else {
          this.redirectToLoginScreen();
        }
      } catch {
        this.redirectToLoginScreen();
      }
    });
  }

  // for testing
  expireSession() {
    deleteCookie(cookieName);
    if (this.loggedIn) {
      this.loggedIn = false;
      this.loginStateChanged.next(false);
    }
  }

  public async getAdToken() {
    try {
      await this.ensureMsalReady();
      // Not specifying an account results in using the active account which is specified
      // by calling `msalService.instance.setActiveAccount` upon login
      return await this.msalService.instance.acquireTokenSilent({ scopes: this.scopes });
    } catch (e) {
      return null;
    }
  }
}
